import { Controller } from "@hotwired/stimulus"

/**
 * Drives the transform-args sub-form inside the pipeline-stage field
 * editor drawer. Swaps the visible input set when the operator picks a
 * different transform from the `<select>`.
 *
 * Each transform exposes its arg schema via the `transformsValue` JSON
 * payload (provided by the ERB from `RawImport::Transforms.list`):
 *
 *   [{ name: "foreign_key",
 *      description: "...",
 *      args_schema: { external_source: :string, external_type: :string, ... } }]
 *
 * Inputs are rendered as `edit[transform_args][<key>]` so they ride
 * through the existing pipeline_stage_edits create action — the
 * controller-side parser merges them with the legacy JSON fallback.
 *
 * Coerce_enum has its own dedicated UI (allowed values, value map,
 * on_unknown, multi/delimiter) and is intentionally bypassed here so
 * the existing structured editor keeps working.
 */
interface ArgSchemaRule {
  type?: "string" | "integer" | "boolean" | "array" | "hash" | "any"
  required?: boolean
}

interface TransformSpec {
  name: string
  description?: string
  args_schema: Record<string, ArgSchemaRule | string>
}

const COERCE_ENUM = "coerce_enum"

export default class extends Controller {
  static targets = [
    "transformSelect",
    "structuredContainer"
  ]

  static values = {
    transforms: { type: String, default: "[]" },
    initialArgs: { type: String, default: "{}" }
  }

  declare readonly transformSelectTarget: HTMLSelectElement
  declare readonly structuredContainerTarget: HTMLElement
  declare transformsValue: string
  declare initialArgsValue: string

  connect(): void {
    this.renderForCurrent()
  }

  transformChanged(): void {
    this.renderForCurrent()
  }

  private renderForCurrent(): void {
    const selected = this.transformSelectTarget.value
    const container = this.structuredContainerTarget
    container.replaceChildren()

    if (!selected) {
      container.classList.add("hidden")
      return
    }

    if (selected === COERCE_ENUM) {
      // The dedicated coerce_enum panel below the structured area handles
      // allowed values + value map. Hide the structured shell entirely.
      container.classList.add("hidden")
      return
    }

    const spec = this.specs.find((s) => s.name === selected)
    if (!spec || !spec.args_schema || Object.keys(spec.args_schema).length === 0) {
      container.classList.remove("hidden")
      const empty = document.createElement("p")
      empty.className = "text-xs text-base-content/60"
      empty.textContent = "No arguments needed for this transform."
      container.appendChild(empty)
      return
    }

    container.classList.remove("hidden")
    const initial = this.parseInitial()
    for (const [key, schema] of Object.entries(spec.args_schema)) {
      container.appendChild(this.fieldFor(key, schema, initial[key]))
    }
  }

  private fieldFor(key: string, raw: ArgSchemaRule | string, currentValue: unknown): HTMLElement {
    const rule: ArgSchemaRule = typeof raw === "string" ? { type: raw as ArgSchemaRule["type"] } : raw
    const label = document.createElement("label")
    label.className = "form-control min-w-0"

    const labelSpan = document.createElement("span")
    labelSpan.className = "label"
    const labelText = document.createElement("span")
    labelText.className = "label-text text-xs"
    labelText.textContent = `${key}${rule.required === false ? " (optional)" : ""}`
    labelSpan.appendChild(labelText)
    label.appendChild(labelSpan)

    const inputName = `edit[transform_args][${key}]`

    if (rule.type === "boolean") {
      const wrapper = document.createElement("div")
      wrapper.className = "flex items-center gap-2"
      const hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = inputName
      hidden.value = "0"
      const checkbox = document.createElement("input")
      checkbox.type = "checkbox"
      checkbox.name = inputName
      checkbox.value = "1"
      checkbox.className = "checkbox checkbox-sm"
      checkbox.checked = currentValue === true || currentValue === "1" || currentValue === "true"
      wrapper.appendChild(hidden)
      wrapper.appendChild(checkbox)
      label.appendChild(wrapper)
    } else if (rule.type === "array" || rule.type === "hash") {
      const textarea = document.createElement("textarea")
      textarea.name = inputName
      textarea.rows = 3
      textarea.className = "textarea textarea-bordered textarea-sm w-full min-w-0 text-xs"
      textarea.placeholder =
        rule.type === "array" ? "One value per line" : "One mapping per line, e.g. source: target"
      textarea.value = this.textareaValueFor(rule.type, currentValue)
      label.appendChild(textarea)
    } else {
      const input = document.createElement("input")
      input.type = rule.type === "integer" ? "number" : "text"
      input.name = inputName
      input.className = "input input-bordered input-sm w-full min-w-0 font-mono text-xs"
      input.value = currentValue === undefined || currentValue === null ? "" : String(currentValue)
      label.appendChild(input)
    }

    return label
  }

  private get specs(): TransformSpec[] {
    try {
      const parsed = JSON.parse(this.transformsValue) as TransformSpec[]
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  private parseInitial(): Record<string, unknown> {
    try {
      const parsed = JSON.parse(this.initialArgsValue) as Record<string, unknown>
      return typeof parsed === "object" && parsed !== null ? parsed : {}
    } catch {
      return {}
    }
  }

  private textareaValueFor(type: ArgSchemaRule["type"], value: unknown): string {
    if (value === undefined || value === null) return ""

    if (type === "array") {
      return Array.isArray(value) ? value.map((entry) => String(entry)).join("\n") : String(value)
    }

    if (type === "hash" && typeof value === "object" && !Array.isArray(value)) {
      return Object.entries(value as Record<string, unknown>)
        .map(([key, entry]) => `${key}: ${String(entry)}`)
        .join("\n")
    }

    return String(value)
  }
}
